import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import scottAvatar from "@/assets/scott-avatar.webp";

export type NavPost = {
  slug: string;
  title: string;
  excerpt?: string | null;
  cover_image_url?: string | null;
  published_at?: string | null;
  category?: { name: string; slug: string } | null;
} | null;

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function NavCard({ post, direction }: { post: NonNullable<NavPost>; direction: "prev" | "next" }) {
  const isNext = direction === "next";
  return (
    <Link
      to="/blog/$slug"
      viewTransition
      params={{ slug: post.slug }}
      className="group block rounded-md border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/30 hover:bg-white/[0.06]"
    >
      <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/75">
        {!isNext && <ArrowLeft className="h-3 w-3" />}
        {isNext ? "Next" : "Previous"}
        {isNext && <ArrowRight className="h-3 w-3" />}
      </span>

      {post.cover_image_url && (
        <div className="mt-3 aspect-[16/9] w-full rounded-md bg-white/5">
          <img
            src={post.cover_image_url}
            alt=""
            loading="lazy"
            decoding="async"
            width={200}
            height={125}
            className="h-full w-full object-cover transition-transform group-hover:scale-110"
          />
        </div>
      )}

      <div className="mt-4 min-w-0">
        {post.category && (
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
            {post.category.name}
          </span>
        )}
        <span
          className="mt-1 block text-base text-white group-hover:underline"
          style={{
            fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
            fontWeight: 700,
          }}
        >
          {post.title}
        </span>
        {post.excerpt && <p className="mt-2 line-clamp-2 text-sm text-white/65">{post.excerpt}</p>}
        <div className="mt-3 flex items-center gap-2 text-[11px] text-white/75">
          <img
            src={scottAvatar}
            alt=""
            loading="lazy"
            decoding="async"
            width={20}
            height={20}
            className="h-5 w-5 rounded-full object-cover"
          />
          <span>Scott Cunningham</span>
          {post.published_at && <span aria-hidden>·</span>}
          {post.published_at && <span>{formatDate(post.published_at)}</span>}
        </div>
      </div>
    </Link>
  );
}

export function PostNav({ prev, next }: { prev: NavPost; next: NavPost }) {
  if (!prev && !next) return null;
  return (
    <nav
      aria-label="Post navigation"
      className="no-print mt-16 grid items-stretch gap-6 border-t border-white/10 pt-10 sm:grid-cols-2"
    >
      {prev ? <NavCard post={prev} direction="prev" /> : <div />}
      {next ? <NavCard post={next} direction="next" /> : <div />}
    </nav>
  );
}
